import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DeptPicker, DeptChips } from "@/components/DeptPicker";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ClipboardList, Plus, CalendarDays, Users, Eye, CheckCircle2, Circle, Trash2, X, UserPlus, Pencil, FileDown, Search } from "lucide-react";
import { PeopleCheckList } from "@/components/PeopleCheckList";

const PRESET_LABELS = ["Aabasoft", "Client"];

function PeoplePicker({ dir, selected, onToggle, testid }) {
  return <PeopleCheckList people={dir} selected={selected} onToggle={onToggle} testid={testid} />;
}

function PointDraftEditor({ dir, points, setPoints }) {
  const [d, setD] = useState({ text: "", memberIds: [], labels: [], labelInput: "" });
  const tgM = (id) => setD((p) => ({ ...p, memberIds: p.memberIds.includes(id) ? p.memberIds.filter((x) => x !== id) : [...p.memberIds, id] }));
  const addLabel = () => { const v = d.labelInput.trim(); if (v) setD({ ...d, labels: [...d.labels, v], labelInput: "" }); };
  const add = () => {
    if (!d.text.trim()) return;
    const labels = [...d.labels];
    if (d.labelInput.trim()) labels.push(d.labelInput.trim());
    setPoints([...points, { text: d.text.trim(), memberIds: d.memberIds, labels }]);
    setD({ text: "", memberIds: [], labels: [], labelInput: "" });
  };
  const nameOf = (id) => dir.find((x) => x.id === id)?.name || id;
  return (
    <div className="space-y-2">
      {points.length > 0 && (
        <div className="space-y-1.5" data-testid="mom-draft-points">
          {points.map((p, i) => (
            <div key={i} className="rounded-lg border border-border p-2.5 text-sm" data-testid={`mom-draft-point-${i}`}>
              <div className="flex items-start gap-2">
                <span className="flex-1 break-words">{p.text}</span>
                <Badge variant="secondary" className="rounded-full text-[10px] bg-amber-500/15 text-amber-600">Open</Badge>
                <button onClick={() => setPoints(points.filter((_, x) => x !== i))} data-testid={`mom-draft-point-remove-${i}`} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
              {(p.memberIds.length + p.labels.length) > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {p.memberIds.map((id) => <Badge key={id} variant="secondary" className="rounded-full text-[10px]">{nameOf(id)}</Badge>)}
                  {p.labels.map((l, j) => <Badge key={j} variant="secondary" className="rounded-full text-[10px]">{l}</Badge>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="rounded-lg bg-secondary/40 p-3 space-y-2">
        <Input value={d.text} onChange={(e) => setD({ ...d, text: e.target.value })} placeholder="Discussion point…" data-testid="mom-create-point-input" />
        <div>
          <Label className="text-[11px]">Responsible — members</Label>
          <div className="mt-1">
            <PeopleCheckList people={dir} selected={d.memberIds} onToggle={tgM} testid="mom-create-point-members" max="max-h-24" />
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Responsible — outside (type a name)</Label>
          <div className="flex gap-2 mt-1">
            <Input value={d.labelInput} onChange={(e) => setD({ ...d, labelInput: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter" && d.labelInput.trim()) { e.preventDefault(); addLabel(); } }} placeholder="e.g. Client name" data-testid="mom-create-point-label-input" />
            <Button type="button" variant="secondary" className="rounded-lg shrink-0" onClick={addLabel} data-testid="mom-create-point-label-add">Add</Button>
          </div>
          {d.labels.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{d.labels.map((l, i) => <Badge key={i} variant="secondary" className="rounded-full text-[11px]">{l}<button className="ml-1" onClick={() => setD({ ...d, labels: d.labels.filter((x) => x !== l) })}><X className="h-3 w-3" /></button></Badge>)}</div>}
        </div>
        <Button type="button" onClick={add} className="rounded-lg w-full" data-testid="mom-create-add-point-btn"><Plus className="h-4 w-4 mr-1" /> Add point</Button>
      </div>
    </div>
  );
}

export default function MomView() {
  const { user } = useAuth();
  const [moms, setMoms] = useState([]);
  const [dir, setDir] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([api.get("/moms"), api.get("/directory")]);
      setMoms(m.data);
      setDir(d.data);
    } catch (e) { toast.error("Could not load MOMs"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><ClipboardList className="h-5 w-5" /></div>
        <div>
          <h2 className="font-heading text-xl font-bold">Minutes of Meeting</h2>
          <p className="text-sm text-muted-foreground">Capture discussion points, assign responsibility, track Open/Closed.</p>
        </div>
        <Button className="ml-auto rounded-lg" onClick={() => setCreating(true)} data-testid="new-mom-btn"><Plus className="h-4 w-4 mr-1.5" /> New MOM</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : moms.length === 0 ? (
        <Card className="p-12 text-center rounded-xl" data-testid="mom-empty"><p className="text-muted-foreground">No meeting minutes yet. Create your first MOM.</p></Card>
      ) : (
        <>
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search meetings by title…" className="pl-9 rounded-lg" data-testid="mom-search-input" />
        </div>
        {(() => {
          const filtered = moms.filter((m) => (m.title || "").toLowerCase().includes(query.trim().toLowerCase()));
          if (filtered.length === 0) return <Card className="p-10 text-center rounded-xl" data-testid="mom-search-empty"><p className="text-muted-foreground">No meetings match “{query}”.</p></Card>;
          return (
          <div className="grid md:grid-cols-2 gap-4" data-testid="mom-list">
            {filtered.map((m) => {
            const openPts = (m.points || []).filter((p) => (p.status || "open") !== "closed").length;
            return (
              <Card key={m.id} className="p-5 rounded-xl cursor-pointer hover:border-primary/40 transition-colors" onClick={() => setActive(m.id)} data-testid={`mom-card-${m.id}`}>
                <div className="flex items-start gap-2">
                  <p className="font-heading font-semibold flex-1">{m.title}</p>
                  {m.unseen && <Badge className="rounded-full border-0 bg-primary text-primary-foreground text-[10px]" data-testid={`mom-unseen-${m.id}`}>New</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{m.meeting_date || "—"}</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{m.attendees.length + (m.external_attendees?.length || 0)} attending</span>
                  {openPts > 0 && <span className="text-primary font-medium">{openPts} open point{openPts > 1 ? "s" : ""}</span>}
                </p>
                <p className="text-[11px] text-muted-foreground mt-2 flex flex-wrap items-center gap-2">By {m.created_by_name} <DeptChips list={m.departments} testid={`mom-depts-${m.id}`} /></p>
              </Card>
            );
          })}
        </div>
          );
        })()}
        </>
      )}

      <CreateMomDialog open={creating} onClose={() => setCreating(false)} dir={dir} onCreated={() => { setCreating(false); load(); }} />
      {active && <MomDetailDialog momId={active} me={user} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function CreateMomDialog({ open, onClose, dir, onCreated }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [externals, setExternals] = useState([]);
  const [extInput, setExtInput] = useState("");
  const [points, setPoints] = useState([]);
  const [depts, setDepts] = useState([]);
  const [saving, setSaving] = useState(false);
  const tg = (setter) => (id) => setter((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const addExt = () => { const v = extInput.trim(); if (v && !externals.includes(v)) setExternals([...externals, v]); setExtInput(""); };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/moms", { title, meeting_date: date, notes, attendee_ids: attendees, viewer_ids: viewers, external_attendees: externals, departments: depts, points: points.map((p) => ({ text: p.text, responsible_user_ids: p.memberIds, responsible_labels: p.labels, status: "open" })) });
      toast.success("MOM created");
      setTitle(""); setNotes(""); setAttendees([]); setViewers([]); setExternals([]); setPoints([]); setDepts([]);
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-mom-dialog">
        <DialogHeader><DialogTitle>New meeting minutes</DialogTitle><DialogDescription>Tag members, add outside attendees, and jot the agenda.</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
          <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="mom-title-input" /></div>
          <div className="space-y-1.5"><Label>Meeting date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mom-date-input" /></div>
          <div className="space-y-1.5"><Label>Departments (optional, multiple)</Label><DeptPicker value={depts} onChange={setDepts} testid="mom-dept-picker" /></div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> Discussion points</Label>
            <PointDraftEditor dir={dir} points={points} setPoints={setPoints} />
          </div>
          <div className="space-y-1.5"><Label className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Attendees (members — can edit)</Label><PeoplePicker dir={dir} selected={attendees} onToggle={tg(setAttendees)} testid="mom-attendees" /></div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Outside attendees (client / vendor)</Label>
            <div className="flex gap-2">
              <Input value={extInput} onChange={(e) => setExtInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExt())} placeholder="Type a name & press Enter" data-testid="mom-external-input" />
              <Button type="button" variant="secondary" className="rounded-lg shrink-0" onClick={addExt} data-testid="mom-external-add">Add</Button>
            </div>
            {externals.length > 0 && (
              <div className="flex flex-wrap gap-1.5">{externals.map((x) => (
                <Badge key={x} variant="secondary" className="rounded-full text-[11px]">{x}<button className="ml-1" onClick={() => setExternals(externals.filter((e) => e !== x))}><X className="h-3 w-3" /></button></Badge>
              ))}</div>
            )}
          </div>
          <div className="space-y-1.5"><Label className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> View-only members</Label><PeoplePicker dir={dir.filter((p) => !attendees.includes(p.id))} selected={viewers} onToggle={tg(setViewers)} testid="mom-viewers" /></div>
          <div className="space-y-1.5"><Label>Notes / agenda (optional)</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mom-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving || !title} className="rounded-lg" data-testid="save-mom-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create MOM"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyPoint = { text: "", memberIds: [], labels: [], labelInput: "" };

export function MomDetailDialog({ momId, me, onClose }) {
  const confirm = useConfirm();
  const [mom, setMom] = useState(null);
  const [pt, setPt] = useState(emptyPoint);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ text: "", memberIds: [], labels: [], labelInput: "" });
  const [todo, setTodo] = useState({ text: "", resp: "", label: "", deadline: "", reminder_at: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/moms/${momId}`); setMom(data); }
    catch (e) { toast.error("Could not load MOM"); onClose(); }
  }, [momId, onClose]);
  useEffect(() => { load(); }, [load]);

  const addPoint = async () => {
    if (!pt.text.trim()) return;
    const labels = [...pt.labels];
    if (pt.labelInput.trim()) labels.push(pt.labelInput.trim());
    try {
      await api.post(`/moms/${momId}/points`, { text: pt.text, responsible_user_ids: pt.memberIds, responsible_labels: labels, status: "open" });
      setPt(emptyPoint); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const togglePointStatus = async (p) => {
    try { await api.patch(`/moms/${momId}/points/${p.id}`, { status: (p.status || "open") === "open" ? "closed" : "open" }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const deletePoint = async (p) => {
    try { await api.delete(`/moms/${momId}/points/${p.id}`); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const addTodo = async () => {
    if (!todo.text.trim()) return;
    const body = { text: todo.text, deadline: todo.deadline, reminder_at: todo.reminder_at ? new Date(todo.reminder_at).toISOString() : "" };
    if (todo.resp) body.responsible_user_id = todo.resp;
    else if (todo.label) body.responsible_label = todo.label;
    try { await api.post(`/moms/${momId}/todos`, body); setTodo({ text: "", resp: "", label: "", deadline: "", reminder_at: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const toggleTodo = async (t) => {
    try { await api.patch(`/moms/${momId}/todos/${t.id}`, { done: !t.done }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const deleteMom = async () => {
    if (!(await confirm({ title: "Delete this MOM?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    try { await api.delete(`/moms/${momId}`); toast.success("MOM deleted"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const startEdit = (p) => { setEditId(p.id); setEdit({ text: p.text, memberIds: [...(p.responsible_user_ids || [])], labels: [...(p.responsible_labels || [])], labelInput: "" }); };
  const tgEditM = (id) => setEdit((e) => ({ ...e, memberIds: e.memberIds.includes(id) ? e.memberIds.filter((x) => x !== id) : [...e.memberIds, id] }));
  const savePointEdit = async (p) => {
    const labels = [...edit.labels];
    if (edit.labelInput.trim()) labels.push(edit.labelInput.trim());
    try {
      await api.patch(`/moms/${momId}/points/${p.id}`, { text: edit.text.trim() || p.text, responsible_user_ids: edit.memberIds, responsible_labels: labels });
      setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const exportPdf = () => {
    if (!mom) return;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const att = [...mom.attendees.map((a) => a.name), ...(mom.external_attendees || []).map((a) => `${a} (outside)`)].join(", ") || "—";
    const viewers = mom.viewers.map((v) => v.name).join(", ");
    const pRows = (mom.points || []).map((p, i) => `<tr><td>${i + 1}</td><td>${esc(p.text)}</td><td>${((p.responsible || []).map(esc).join(", ")) || "—"}</td><td class="st ${(p.status || "open") === "closed" ? "cl" : "op"}">${(p.status || "open") === "closed" ? "Closed" : "Open"}</td></tr>`).join("");
    const tRows = (mom.todos || []).map((t, i) => `<tr><td>${i + 1}</td><td>${esc(t.text)}</td><td>${esc(t.responsible_name || "—")}</td><td>${esc(t.deadline || "—")}</td><td>${t.done ? "Done" : "Pending"}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(mom.title)} — MOM</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:32px;font-size:13px}
h1{font-size:20px;margin:0 0 2px}.sub{color:#555;font-size:12px;margin-bottom:16px}
.meta{border:1px solid #ddd;border-radius:8px;padding:12px;margin-bottom:18px;font-size:12px;line-height:1.6}
.meta b{display:inline-block;min-width:120px;color:#333}
h2{font-size:14px;margin:18px 0 8px;border-bottom:2px solid #e11d48;padding-bottom:4px;color:#111}
table{width:100%;border-collapse:collapse;margin-bottom:8px}
th,td{border:1px solid #ccc;padding:7px 9px;text-align:left;vertical-align:top}
th{background:#f4f4f5;font-size:11px;text-transform:uppercase;letter-spacing:.03em}
td:first-child,th:first-child{width:36px;text-align:center}
.st{font-weight:bold}.op{color:#b45309}.cl{color:#047857}
.footer{margin-top:28px;color:#888;font-size:10px;text-align:center;border-top:1px solid #eee;padding-top:8px}
@media print{body{margin:12mm}}
</style></head><body>
<h1>${esc(mom.title)}</h1>
<div class="sub">Minutes of Meeting · ABS Pulse</div>
<div class="meta">
<div><b>Meeting date:</b> ${esc(mom.meeting_date || "—")}</div>
<div><b>Recorded by:</b> ${esc(mom.created_by_name)}</div>
<div><b>Attendees:</b> ${esc(att)}</div>
${viewers ? `<div><b>Viewers:</b> ${esc(viewers)}</div>` : ""}
${mom.notes ? `<div><b>Notes:</b> ${esc(mom.notes)}</div>` : ""}
</div>
<h2>Discussion Points</h2>
<table><thead><tr><th>#</th><th>Discussion Point</th><th>Responsibility</th><th>Status</th></tr></thead>
<tbody>${pRows || '<tr><td colspan="4" style="text-align:center;color:#888">No points</td></tr>'}</tbody></table>
${(mom.todos || []).length ? `<h2>Action Items</h2><table><thead><tr><th>#</th><th>Action</th><th>Responsible</th><th>Deadline</th><th>Status</th></tr></thead><tbody>${tRows}</tbody></table>` : ""}
<div class="footer">© 2026 Aabasoft. All Rights Reserved. | ABS Pulse — Proprietary &amp; Confidential | Unauthorized copying, sharing, reproduction, or use is prohibited.</div>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to export"); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  const canEdit = mom?.can_edit;
  const tgMember = (id) => setPt((p) => ({ ...p, memberIds: p.memberIds.includes(id) ? p.memberIds.filter((x) => x !== id) : [...p.memberIds, id] }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mom-detail-dialog">
        {!mom ? (
          <>
            <DialogHeader><DialogTitle>Loading…</DialogTitle><DialogDescription>Fetching meeting minutes.</DialogDescription></DialogHeader>
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          </>
        ) : (
          <div className="max-h-[80vh] overflow-auto pr-1">
            <DialogHeader>
              <DialogTitle>{mom.title}</DialogTitle>
              <DialogDescription>{mom.meeting_date || "—"} · by {mom.created_by_name}{!canEdit && " · view only"}</DialogDescription>
            </DialogHeader>
            {(me.is_owner || mom.created_by === me.id) && (
              <Button variant="ghost" size="sm" className="text-destructive w-fit -mt-1 h-7 px-2" onClick={deleteMom} data-testid="delete-mom-btn"><Trash2 className="h-4 w-4 mr-1" /> Delete MOM</Button>
            )}
            <Button variant="outline" size="sm" className="w-fit rounded-lg h-7 px-2 mt-1" onClick={exportPdf} data-testid="export-mom-btn"><FileDown className="h-4 w-4 mr-1" /> Export PDF</Button>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {mom.attendees.map((a) => <Badge key={a.id} variant="secondary" className="rounded-full text-[11px]">{a.name}</Badge>)}
              {(mom.external_attendees || []).map((a) => <Badge key={a} variant="secondary" className="rounded-full text-[11px] bg-amber-500/15 text-amber-700">{a} · outside</Badge>)}
              {mom.viewers.map((a) => <Badge key={a.id} variant="outline" className="rounded-full text-[11px] opacity-70">{a.name} · view</Badge>)}
            </div>

            {/* Discussion points */}
            <h4 className="font-heading font-semibold text-sm mt-5 mb-2">Discussion points</h4>
            <div className="space-y-2" data-testid="mom-points">
              {(mom.points || []).length === 0 && <p className="text-xs text-muted-foreground">No points yet.</p>}
              {(mom.points || []).map((p) => {
                const closed = (p.status || "open") === "closed";
                return (
                  <div key={p.id} className="rounded-lg border border-border p-3" data-testid={`mom-point-${p.id}`}>
                    <div className="flex items-start gap-2">
                      <p className={`text-sm flex-1 break-words ${closed ? "line-through text-muted-foreground" : ""}`}>{p.text}</p>
                      <button onClick={() => canEdit && togglePointStatus(p)} disabled={!canEdit} data-testid={`point-status-${p.id}`}
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${closed ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                        {closed ? "Closed" : "Open"}
                      </button>
                      {canEdit && <button onClick={() => (editId === p.id ? setEditId(null) : startEdit(p))} className="text-muted-foreground hover:text-primary shrink-0" data-testid={`point-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>}
                      {canEdit && <button onClick={() => deletePoint(p)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`point-delete-${p.id}`}><X className="h-4 w-4" /></button>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className="text-[11px] text-muted-foreground">Responsible:</span>
                      {(p.responsible || []).length === 0 && <span className="text-[11px] text-muted-foreground">—</span>}
                      {(p.responsible || []).map((r, i) => <Badge key={i} variant="secondary" className="rounded-full text-[10px]">{r}</Badge>)}
                    </div>
                    {editId === p.id && canEdit && (
                      <div className="mt-2 space-y-2 rounded-lg bg-secondary/40 p-2.5" data-testid={`point-edit-form-${p.id}`}>
                        <Input value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} data-testid={`point-edit-text-${p.id}`} />
                        <div>
                          <Label className="text-[11px]">Responsible — members</Label>
                          <div className="mt-1">
                            {mom.attendees.length === 0 ? <p className="text-[11px] text-muted-foreground">No member attendees.</p>
                              : <PeopleCheckList people={mom.attendees} selected={edit.memberIds} onToggle={tgEditM} testid={`point-edit-members-${p.id}`} max="max-h-24" />}
                          </div>
                        </div>
                        <div>
                          <Label className="text-[11px]">Responsible — outside</Label>
                          <div className="flex gap-2 mt-1">
                            <Input value={edit.labelInput} onChange={(e) => setEdit({ ...edit, labelInput: e.target.value })}
                              onKeyDown={(e) => { if (e.key === "Enter" && edit.labelInput.trim()) { e.preventDefault(); setEdit({ ...edit, labels: [...edit.labels, edit.labelInput.trim()], labelInput: "" }); } }}
                              placeholder="e.g. Client name" data-testid={`point-edit-label-input-${p.id}`} />
                            <Button type="button" variant="secondary" className="rounded-lg shrink-0" onClick={() => edit.labelInput.trim() && setEdit({ ...edit, labels: [...edit.labels, edit.labelInput.trim()], labelInput: "" })}>Add</Button>
                          </div>
                          {edit.labels.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{edit.labels.map((l, i) => (
                            <Badge key={i} variant="secondary" className="rounded-full text-[11px]">{l}<button className="ml-1" onClick={() => setEdit({ ...edit, labels: edit.labels.filter((x) => x !== l) })}><X className="h-3 w-3" /></button></Badge>
                          ))}</div>}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="rounded-lg flex-1" onClick={() => savePointEdit(p)} data-testid={`point-edit-save-${p.id}`}>Save</Button>
                          <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setEditId(null)} data-testid={`point-edit-cancel-${p.id}`}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {canEdit && (
              <div className="mt-3 space-y-2 rounded-lg bg-secondary/40 p-3" data-testid="add-point-form">
                <Input value={pt.text} onChange={(e) => setPt({ ...pt, text: e.target.value })} placeholder="New discussion point…" data-testid="mom-point-input" />
                <div>
                  <Label className="text-[11px]">Responsible — members</Label>
                  <div className="mt-1">
                    {mom.attendees.length === 0 ? <p className="text-[11px] text-muted-foreground">No member attendees.</p>
                      : <PeopleCheckList people={mom.attendees} selected={pt.memberIds} onToggle={tgMember} testid="point-members" max="max-h-28" />}
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Responsible — outside (type a name)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={pt.labelInput} onChange={(e) => setPt({ ...pt, labelInput: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter" && pt.labelInput.trim()) { e.preventDefault(); setPt({ ...pt, labels: [...pt.labels, pt.labelInput.trim()], labelInput: "" }); } }}
                      placeholder="e.g. Client name" data-testid="point-label-input" />
                    <Button type="button" variant="secondary" className="rounded-lg shrink-0" onClick={() => pt.labelInput.trim() && setPt({ ...pt, labels: [...pt.labels, pt.labelInput.trim()], labelInput: "" })} data-testid="point-label-add">Add</Button>
                  </div>
                  {pt.labels.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{pt.labels.map((l, i) => (
                    <Badge key={i} variant="secondary" className="rounded-full text-[11px]">{l}<button className="ml-1" onClick={() => setPt({ ...pt, labels: pt.labels.filter((x) => x !== l) })}><X className="h-3 w-3" /></button></Badge>
                  ))}</div>}
                </div>
                <Button onClick={addPoint} className="rounded-lg w-full" data-testid="add-point-btn"><Plus className="h-4 w-4 mr-1" /> Add point</Button>
              </div>
            )}

            {/* Action items (to-dos with deadlines) */}
            <h4 className="font-heading font-semibold text-sm mt-6 mb-2">Action items (with deadlines)</h4>
            <div className="space-y-2" data-testid="mom-todos">
              {(mom.todos || []).length === 0 && <p className="text-xs text-muted-foreground">No action items yet.</p>}
              {(mom.todos || []).map((t) => (
                <div key={t.id} className="flex items-start gap-2 rounded-lg border border-border p-2.5" data-testid={`mom-todo-${t.id}`}>
                  <button onClick={() => canEdit && toggleTodo(t)} disabled={!canEdit} className="mt-0.5 shrink-0" data-testid={`todo-toggle-${t.id}`}>
                    {t.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm break-words ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.text}</p>
                    <p className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2">
                      {t.responsible_name && <span>👤 {t.responsible_name}</span>}
                      {t.deadline && <span>📅 {t.deadline}</span>}
                      {t.done && t.done_by_name && <span>✓ by {t.done_by_name}</span>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="mt-3 space-y-2 rounded-lg bg-secondary/40 p-3" data-testid="add-todo-form">
                <Input value={todo.text} onChange={(e) => setTodo({ ...todo, text: e.target.value })} placeholder="New action item…" data-testid="todo-text-input" />
                <div className="grid grid-cols-2 gap-2">
                  <select className="h-9 rounded-lg border border-border bg-background px-2 text-sm" value={todo.resp ? `u:${todo.resp}` : (todo.label ? `l:${todo.label}` : "")}
                    onChange={(e) => { const v = e.target.value; if (v.startsWith("u:")) setTodo({ ...todo, resp: v.slice(2), label: "" }); else if (v.startsWith("l:")) setTodo({ ...todo, resp: "", label: v.slice(2) }); else setTodo({ ...todo, resp: "", label: "" }); }}
                    data-testid="todo-responsible-select">
                    <option value="">— Responsible —</option>
                    {mom.attendees.map((a) => <option key={a.id} value={`u:${a.id}`}>{a.name}</option>)}
                    {PRESET_LABELS.map((l) => <option key={l} value={`l:${l}`}>{l}</option>)}
                  </select>
                  <Input value={todo.label && !PRESET_LABELS.includes(todo.label) ? todo.label : ""} onChange={(e) => setTodo({ ...todo, label: e.target.value, resp: "" })} placeholder="or custom label" data-testid="todo-custom-label-input" />
                  <div><Label className="text-[11px]">Deadline</Label><Input type="date" value={todo.deadline} onChange={(e) => setTodo({ ...todo, deadline: e.target.value })} data-testid="todo-deadline-input" /></div>
                  <div><Label className="text-[11px]">Reminder</Label><Input type="datetime-local" value={todo.reminder_at} onChange={(e) => setTodo({ ...todo, reminder_at: e.target.value })} data-testid="todo-reminder-input" /></div>
                </div>
                <Button onClick={addTodo} className="rounded-lg w-full" data-testid="add-todo-btn"><Plus className="h-4 w-4 mr-1" /> Add action item</Button>
              </div>
            )}

            {/* Notes (at the bottom) */}
            <h4 className="font-heading font-semibold text-sm mt-6 mb-2">Notes</h4>
            {mom.notes ? (
              <p className="text-sm text-foreground/80 whitespace-pre-wrap rounded-lg bg-secondary/40 p-3" data-testid="mom-notes-display">{mom.notes}</p>
            ) : (
              <p className="text-xs text-muted-foreground">No notes.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
